import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const PRESENCE_WS_BASE = 'wss://hk0712-theralingua-presence-gateway.hf.space/ws/presence';

const buildPresenceWsUrl = (userId) => {
  const base = (PRESENCE_WS_BASE || '').trim();
  if (!base || !userId) return '';
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}user_id=${encodeURIComponent(userId)}`;
};

export default function usePresenceGateway(currentUserId, enabled = true) {
  const [onlineUserMap, setOnlineUserMap] = useState({});
  const [lastOnlineMap, setLastOnlineMap] = useState({});
  const [connectionState, setConnectionState] = useState('idle');

  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);

  const cleanupReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const scheduleReconnect = useCallback(() => {
    cleanupReconnectTimer();
    reconnectAttemptRef.current += 1;
    const attempt = reconnectAttemptRef.current;
    const delayMs = Math.min(10000, 800 * attempt);

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      setConnectionState('reconnecting');
      const socket = socketRef.current;
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        setConnectionState('idle');
      }
    }, delayMs);
  }, [cleanupReconnectTimer]);

  useEffect(() => {
    if (!enabled || !currentUserId) {
      cleanupReconnectTimer();
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setConnectionState('idle');
      return undefined;
    }

    let manuallyClosed = false;

    const connect = () => {
      const wsUrl = buildPresenceWsUrl(currentUserId);
      if (!wsUrl) return;

      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        reconnectAttemptRef.current = 0;
        setConnectionState('connected');
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const type = String(payload.type || '');
          const userId = String(payload.user_id || '').trim();

          if (type === 'presence_snapshot') {
            const next = {};
            const onlineUsers = Array.isArray(payload.online_users) ? payload.online_users : [];
            onlineUsers.forEach((id) => {
              const normalized = String(id || '').trim();
              if (!normalized) return;
              next[normalized] = true;
            });
            setOnlineUserMap(next);
            return;
          }

          if (type === 'presence_online' && userId) {
            setOnlineUserMap((prev) => ({ ...prev, [userId]: true }));
            return;
          }

          if (type === 'presence_offline' && userId) {
            setOnlineUserMap((prev) => {
              const next = { ...prev };
              delete next[userId];
              return next;
            });
            if (payload.last_online_at) {
              setLastOnlineMap((prev) => ({ ...prev, [userId]: payload.last_online_at }));
            }
          }
        } catch {
          // ignore malformed payload
        }
      };

      socket.onclose = () => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }
        if (!manuallyClosed) {
          setConnectionState('disconnected');
          scheduleReconnect();
          setTimeout(connect, Math.min(10000, 800 * Math.max(1, reconnectAttemptRef.current)));
        }
      };

      socket.onerror = () => {
        setConnectionState('error');
      };
    };

    connect();

    return () => {
      manuallyClosed = true;
      cleanupReconnectTimer();
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setConnectionState('idle');
    };
  }, [cleanupReconnectTimer, currentUserId, enabled, scheduleReconnect]);

  const isUserOnline = useCallback((userId) => {
    const normalized = String(userId || '').trim();
    return !!onlineUserMap[normalized];
  }, [onlineUserMap]);

  const getLastOnline = useCallback((userId) => {
    const normalized = String(userId || '').trim();
    return lastOnlineMap[normalized] || null;
  }, [lastOnlineMap]);

  return useMemo(() => ({
    isUserOnline,
    getLastOnline,
    onlineUserMap,
    lastOnlineMap,
    connectionState,
  }), [connectionState, getLastOnline, isUserOnline, lastOnlineMap, onlineUserMap]);
}
